// DFWPortalPage.js — DFW Airport Fiber Contractor Portal
// Full portal for DFW 1099 workers: daily reports (hours + production),
// compliance tracking, site rules acknowledgment, project info & contacts.
import React, { useState, useEffect, useCallback } from 'react';
import {
  Clock, Briefcase, Shield, ArrowLeft, Lock, Send, Loader,
  CheckCircle, Phone, Mail, Sun, Moon, ChevronDown,
  AlertTriangle, DollarSign, MapPin, User, Calendar,
  Activity, Info, Check, XCircle, ChevronRight, Zap,
  HardHat, FileText, Target, Award, Ruler, Wrench
} from 'lucide-react';
import { DFW_PORTAL_CONFIG } from '../config/constants';
import { useMobile } from '../utils/theme';

const API_BASE = '/api/daily-reports';
const STORAGE_KEY = 'lyt_dfw_worker';
const COMPLIANCE_KEY_PREFIX = 'lyt_dfw_compliance_';
const RULES_ACK_KEY_PREFIX = 'lyt_dfw_rules_ack_';

// ── SVG progress ring ──
const ProgressRing = ({ pct, size = 80, stroke = 6, color = '#3b82f6', trackColor = '#334155', textColor = '#fff' }) => {
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const offset = circ - (pct / 100) * circ;
  return (
    <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={trackColor} strokeWidth={stroke} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={color} strokeWidth={stroke}
        strokeDasharray={circ} strokeDashoffset={offset} strokeLinecap="round"
        style={{ transition: 'stroke-dashoffset 0.6s ease' }} />
      <text x={size / 2} y={size / 2} textAnchor="middle" dominantBaseline="central"
        fill={textColor} fontSize={size * 0.22} fontWeight="700"
        style={{ transform: 'rotate(90deg)', transformOrigin: 'center' }}>
        {pct}%
      </text>
    </svg>
  );
};

const DFWPortalPage = ({ darkMode, setDarkMode, setCurrentPage, loggedInUser }) => {
  const cfg = DFW_PORTAL_CONFIG;
  const isMobile = useMobile();

  // ── Theme palette ──
  const t = {
    bg: darkMode ? '#0b1120' : '#f0f4f8',
    cardBg: darkMode ? '#151d2e' : '#ffffff',
    cardBgElevated: darkMode ? '#1a2437' : '#ffffff',
    text: darkMode ? '#e8ecf1' : '#1a202c',
    textSecondary: darkMode ? '#94a3b8' : '#64748b',
    textMuted: darkMode ? '#64748b' : '#94a3b8',
    border: darkMode ? '#1e293b' : '#e2e8f0',
    borderLight: darkMode ? '#253347' : '#edf2f7',
    inputBg: darkMode ? '#0f1729' : '#f7f9fb',
    accent: '#3b82f6',
    accentDark: '#2563eb',
    accentLight: darkMode ? '#1e3a5f' : '#dbeafe',
    success: '#10b981',
    successBg: darkMode ? '#052e1c' : '#ecfdf5',
    successText: darkMode ? '#6ee7b7' : '#065f46',
    warning: '#f59e0b',
    warningBg: darkMode ? '#422006' : '#fffbeb',
    warningText: darkMode ? '#fcd34d' : '#92400e',
    danger: '#ef4444',
    dangerBg: darkMode ? '#450a0a' : '#fef2f2',
    dangerText: darkMode ? '#fca5a5' : '#991b1b',
    gradientHeader: darkMode
      ? 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)'
      : 'linear-gradient(135deg, #1e3a5f 0%, #1e293b 100%)',
    gradientAccent: darkMode
      ? 'linear-gradient(135deg, #3b82f6, #8b5cf6)'
      : 'linear-gradient(135deg, #2563eb, #3b82f6)',
    gradientSuccess: darkMode
      ? 'linear-gradient(135deg, #059669, #10b981)'
      : 'linear-gradient(135deg, #059669, #34d399)',
    gradientCard: darkMode
      ? 'linear-gradient(135deg, #1a2437 0%, #151d2e 100%)'
      : 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
    shadow: darkMode ? '0 2px 8px rgba(0,0,0,0.4)' : '0 1px 3px rgba(0,0,0,0.08), 0 1px 2px rgba(0,0,0,0.04)',
    shadowMd: darkMode ? '0 4px 16px rgba(0,0,0,0.5)' : '0 4px 6px rgba(0,0,0,0.07), 0 2px 4px rgba(0,0,0,0.04)',
    shadowLg: darkMode ? '0 8px 32px rgba(0,0,0,0.6)' : '0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)',
  };

  // ── Access gate ──
  const [unlocked, setUnlocked] = useState(!!loggedInUser);
  const [codeInput, setCodeInput] = useState('');
  const [codeError, setCodeError] = useState('');

  // ── Worker identity ──
  const [workerName, setWorkerName] = useState('');
  const [workerEmail, setWorkerEmail] = useState('');
  const [workerRole, setWorkerRole] = useState('');
  const [identitySet, setIdentitySet] = useState(false);

  // ── Tab navigation ──
  const [activeTab, setActiveTab] = useState('report');

  // ── Daily report form ──
  const [reportDate, setReportDate] = useState(new Date().toISOString().split('T')[0]);
  const [startTime, setStartTime] = useState('22:00');
  const [endTime, setEndTime] = useState('06:00');
  const [hoursWorked, setHoursWorked] = useState('');
  const [area, setArea] = useState('');
  const [selectedWorkTypes, setSelectedWorkTypes] = useState([]);
  const [cablePulled, setCablePulled] = useState('');
  const [splicesCompleted, setSplicesCompleted] = useState('');
  const [traysInstalled, setTraysInstalled] = useState('');
  const [jhooksInstalled, setJhooksInstalled] = useState('');
  const [termsCompleted, setTermsCompleted] = useState('');
  const [testsCompleted, setTestsCompleted] = useState('');
  const [sleevesInstalled, setSleevesInstalled] = useState('');
  const [equipmentUsed, setEquipmentUsed] = useState([]);
  const [safetyNotes, setSafetyNotes] = useState('');
  const [incidents, setIncidents] = useState('');
  const [reportNotes, setReportNotes] = useState('');

  // ── Report history ──
  const [reports, setReports] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // ── Compliance ──
  const [compliance, setCompliance] = useState({});

  // ── Site rules ──
  const [rulesAcknowledged, setRulesAcknowledged] = useState(false);
  const [expandedRuleCategory, setExpandedRuleCategory] = useState(null);

  // ── UI ──
  const [submitting, setSubmitting] = useState(false);
  const [toast, setToast] = useState(null);
  const [showHistory, setShowHistory] = useState(false);

  // ── Init: load saved identity ──
  useEffect(() => {
    // Check localStorage first for saved DFW worker identity (has role info)
    const saved = localStorage.getItem(STORAGE_KEY);
    let savedData = null;
    if (saved) {
      try { savedData = JSON.parse(saved); } catch (e) { /* ignore */ }
    }

    if (loggedInUser) {
      setUnlocked(true);
      setWorkerName(loggedInUser.name || '');
      setWorkerEmail((loggedInUser.email || '').toLowerCase());
      // If we have a saved role from a previous session, use it
      if (savedData && savedData.role && savedData.email === (loggedInUser.email || '').toLowerCase()) {
        setWorkerRole(savedData.role);
        setIdentitySet(true);
      }
      // Otherwise leave identitySet false so the role picker shows
    } else if (savedData) {
      setWorkerName(savedData.name || '');
      setWorkerEmail(savedData.email || '');
      setWorkerRole(savedData.role || '');
      setIdentitySet(true);
      setUnlocked(true);
    }
  }, [loggedInUser]);

  // ── Load compliance from localStorage ──
  useEffect(() => {
    if (workerEmail) {
      const saved = localStorage.getItem(COMPLIANCE_KEY_PREFIX + workerEmail);
      if (saved) try { setCompliance(JSON.parse(saved)); } catch (e) { /* ignore */ }
      const rulesAck = localStorage.getItem(RULES_ACK_KEY_PREFIX + workerEmail);
      if (rulesAck === 'true') setRulesAcknowledged(true);
    }
  }, [workerEmail]);

  // ── Save compliance ──
  useEffect(() => {
    if (workerEmail && Object.keys(compliance).length > 0) {
      localStorage.setItem(COMPLIANCE_KEY_PREFIX + workerEmail, JSON.stringify(compliance));
    }
  }, [compliance, workerEmail]);

  // ── Load report history ──
  const loadHistory = useCallback(async () => {
    if (!workerEmail) return;
    setHistoryLoading(true);
    try {
      const res = await fetch(`${API_BASE}?worker_email=${encodeURIComponent(workerEmail)}&project_id=${cfg.projectId}`);
      const data = await res.json();
      if (data.success) setReports(data.data || []);
    } catch (e) {
      console.error('Failed to load history:', e);
    }
    setHistoryLoading(false);
  }, [workerEmail, cfg.projectId]);

  useEffect(() => {
    if (identitySet && workerEmail) loadHistory();
  }, [identitySet, workerEmail, loadHistory]);

  // ── Toast helper ──
  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 4000);
  };

  // ── Access code handler ──
  const handleCodeSubmit = (e) => {
    e.preventDefault();
    if (codeInput.trim().toLowerCase() === cfg.accessCode) {
      setUnlocked(true);
      setCodeError('');
    } else {
      setCodeError('Invalid access code');
    }
  };

  // ── Identity save ──
  const handleIdentitySave = (e) => {
    e.preventDefault();
    if (!workerName.trim() || !workerEmail.trim() || !workerRole) return;
    const data = { name: workerName.trim(), email: workerEmail.trim().toLowerCase(), role: workerRole };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    setWorkerEmail(data.email);
    setIdentitySet(true);
  };

  // ── Work type toggle ──
  const toggleWorkType = (wt) => {
    setSelectedWorkTypes(prev =>
      prev.includes(wt) ? prev.filter(w => w !== wt) : [...prev, wt]
    );
  };

  // ── Equipment toggle ──
  const toggleEquipment = (eq) => {
    setEquipmentUsed(prev =>
      prev.includes(eq) ? prev.filter(e => e !== eq) : [...prev, eq]
    );
  };

  // ── Submit daily report ──
  const handleSubmitReport = async (e) => {
    e.preventDefault();
    if (!hoursWorked || parseFloat(hoursWorked) <= 0) {
      showToast('Enter hours worked', 'error');
      return;
    }
    if (!area) {
      showToast('Select the area you worked in', 'error');
      return;
    }
    if (selectedWorkTypes.length === 0) {
      showToast('Select at least one work type', 'error');
      return;
    }
    if (!rulesAcknowledged) {
      showToast('You must acknowledge the DFW site rules before submitting', 'error');
      setActiveTab('rules');
      return;
    }

    setSubmitting(true);
    const roleInfo = cfg.roles[workerRole] || {};
    const payload = {
      project_id: cfg.projectId,
      worker_name: workerName,
      worker_email: workerEmail,
      worker_role: roleInfo.title || workerRole,
      hourly_rate: roleInfo.rate || 0,
      work_date: reportDate,
      start_time: startTime,
      end_time: endTime,
      hours_worked: parseFloat(hoursWorked),
      area,
      work_types: selectedWorkTypes.join(', '),
      cable_pulled_ft: parseFloat(cablePulled) || 0,
      splices_completed: parseInt(splicesCompleted) || 0,
      trays_installed_ft: parseFloat(traysInstalled) || 0,
      jhooks_installed: parseInt(jhooksInstalled) || 0,
      terms_completed: parseInt(termsCompleted) || 0,
      tests_completed: parseInt(testsCompleted) || 0,
      sleeves_installed: parseInt(sleevesInstalled) || 0,
      equipment_used: equipmentUsed.join(', '),
      safety_notes: safetyNotes,
      incidents,
      notes: reportNotes,
      site_rules_acknowledged: rulesAcknowledged,
    };

    try {
      const res = await fetch(API_BASE, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (data.success) {
        showToast('Daily report submitted successfully');
        setCablePulled(''); setSplicesCompleted(''); setTraysInstalled('');
        setJhooksInstalled(''); setTermsCompleted(''); setTestsCompleted('');
        setSleevesInstalled(''); setSafetyNotes(''); setIncidents('');
        setReportNotes(''); setSelectedWorkTypes([]); setEquipmentUsed([]);
        setHoursWorked('');
        loadHistory();
      } else {
        showToast(data.error || 'Failed to submit', 'error');
      }
    } catch (err) {
      showToast('Network error — try again', 'error');
    }
    setSubmitting(false);
  };

  // ── Compliance toggle ──
  const toggleCompliance = (id) => {
    setCompliance(prev => ({ ...prev, [id]: !prev[id] }));
  };

  // ── Acknowledge site rules ──
  const handleAcknowledgeRules = () => {
    setRulesAcknowledged(true);
    if (workerEmail) {
      localStorage.setItem(RULES_ACK_KEY_PREFIX + workerEmail, 'true');
    }
    showToast('Site rules acknowledged');
  };

  // ── Helper: get role info ──
  const roleInfo = cfg.roles[workerRole] || {};
  const hourlyRate = roleInfo.rate || 0;

  // ── Weekly summary from history ──
  const getWeeklySummary = () => {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay());
    startOfWeek.setHours(0, 0, 0, 0);
    const weekReports = reports.filter(r => new Date(r.work_date) >= startOfWeek);
    const totalHours = weekReports.reduce((s, r) => s + parseFloat(r.hours_worked || 0), 0);
    const totalPay = weekReports.reduce((s, r) => s + parseFloat(r.total_pay || 0), 0);
    const totalCable = weekReports.reduce((s, r) => s + parseFloat(r.cable_pulled_ft || 0), 0);
    const totalSplices = weekReports.reduce((s, r) => s + parseInt(r.splices_completed || 0), 0);
    return { count: weekReports.length, totalHours, totalPay, totalCable, totalSplices };
  };

  // ── Compliance progress ──
  const complianceCount = cfg.complianceItems.filter(item => compliance[item.id]).length;
  const complianceTotal = cfg.complianceItems.length;
  const compliancePct = Math.round((complianceCount / complianceTotal) * 100);

  // ── Reusable styles ──
  const card = {
    background: t.cardBg,
    borderRadius: 16,
    border: `1px solid ${t.border}`,
    padding: isMobile ? 16 : 24,
    marginBottom: 16,
    boxShadow: t.shadow,
  };

  const cardElevated = {
    ...card,
    background: t.cardBgElevated,
    boxShadow: t.shadowMd,
  };

  const label = {
    display: 'block',
    fontSize: 13,
    color: t.textSecondary,
    marginBottom: 6,
    fontWeight: 600,
    letterSpacing: '0.02em',
    textTransform: 'uppercase',
  };

  const labelNormal = {
    display: 'block',
    fontSize: 13,
    color: t.textSecondary,
    marginBottom: 4,
    fontWeight: 500,
  };

  const input = {
    width: '100%',
    padding: '11px 14px',
    borderRadius: 10,
    border: `1.5px solid ${t.border}`,
    background: t.inputBg,
    color: t.text,
    fontSize: 15,
    outline: 'none',
    boxSizing: 'border-box',
    transition: 'border-color 0.2s, box-shadow 0.2s',
  };

  const select = { ...input, cursor: 'pointer', appearance: 'none' };

  const btnPrimary = {
    background: t.gradientAccent,
    color: '#fff',
    border: 'none',
    borderRadius: 12,
    padding: '14px 28px',
    fontSize: 15,
    fontWeight: 700,
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 10,
    justifyContent: 'center',
    width: '100%',
    minHeight: 52,
    opacity: submitting ? 0.7 : 1,
    boxShadow: '0 4px 14px rgba(59,130,246,0.35)',
    transition: 'opacity 0.2s, transform 0.1s',
    letterSpacing: '0.01em',
  };

  const chip = (active) => ({
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    padding: '7px 14px',
    borderRadius: 24,
    fontSize: 13,
    cursor: 'pointer',
    border: active ? `1.5px solid ${t.accent}` : `1.5px solid ${t.border}`,
    background: active ? t.accentLight : 'transparent',
    color: active ? t.accent : t.textSecondary,
    fontWeight: active ? 600 : 400,
    margin: '0 6px 8px 0',
    transition: 'all 0.15s ease',
    userSelect: 'none',
    boxShadow: active ? `0 0 0 3px ${darkMode ? 'rgba(59,130,246,0.15)' : 'rgba(59,130,246,0.1)'}` : 'none',
  });

  const sectionHeader = (text, icon) => (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 16 }}>
      <div style={{
        width: 36, height: 36, borderRadius: 10,
        background: t.accentLight,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {icon}
      </div>
      <h3 style={{ color: t.text, margin: 0, fontSize: 17, fontWeight: 700 }}>{text}</h3>
    </div>
  );

  // ══════════════════════════════════════════════
  // ACCESS CODE GATE
  // ══════════════════════════════════════════════
  if (!unlocked) {
    return (
      <div style={{
        minHeight: '100vh',
        background: darkMode
          ? 'linear-gradient(180deg, #0b1120 0%, #111827 100%)'
          : 'linear-gradient(180deg, #e0e7ff 0%, #f0f4f8 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}>
        <div style={{
          ...cardElevated, maxWidth: 420, width: '100%', textAlign: 'center',
          padding: isMobile ? 32 : 40, boxShadow: t.shadowLg,
        }}>
          {/* Logo */}
          <div style={{ marginBottom: 24 }}>
            <div style={{
              width: 72, height: 72, borderRadius: 20, margin: '0 auto 16px',
              background: t.gradientAccent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 8px 24px rgba(59,130,246,0.3)',
            }}>
              <Zap size={32} color="#fff" />
            </div>
            <h1 style={{
              color: t.text, margin: '0 0 4px', fontSize: 22, fontWeight: 800,
              letterSpacing: '-0.02em',
            }}>
              DFW Airport Portal
            </h1>
            <p style={{
              color: t.textSecondary, margin: 0, fontSize: 14,
            }}>
              <span style={{ fontWeight: 700 }}>LYT</span> Communications
            </p>
          </div>

          <div style={{
            width: 40, height: 2, background: t.gradientAccent,
            margin: '0 auto 24px', borderRadius: 1,
          }} />

          <p style={{ color: t.textSecondary, margin: '0 0 24px', fontSize: 14, lineHeight: 1.5 }}>
            Enter your access code to sign in to the contractor portal.
          </p>

          <form onSubmit={handleCodeSubmit}>
            <input
              type="text" value={codeInput} onChange={e => setCodeInput(e.target.value)}
              placeholder="Enter access code" autoFocus
              style={{
                ...input, textAlign: 'center', fontSize: 20, letterSpacing: 3,
                marginBottom: 16, padding: '14px 16px', fontWeight: 600,
              }}
            />
            {codeError && (
              <div style={{
                background: t.dangerBg, color: t.dangerText, borderRadius: 8,
                padding: '8px 12px', fontSize: 13, marginBottom: 16,
                display: 'flex', alignItems: 'center', gap: 6, justifyContent: 'center',
              }}>
                <XCircle size={14} /> {codeError}
              </div>
            )}
            <button type="submit" style={btnPrimary}>
              <Lock size={18} /> Enter Portal
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════
  // IDENTITY SETUP
  // ══════════════════════════════════════════════
  if (!identitySet) {
    return (
      <div style={{
        minHeight: '100vh',
        background: darkMode
          ? 'linear-gradient(180deg, #0b1120 0%, #111827 100%)'
          : 'linear-gradient(180deg, #e0e7ff 0%, #f0f4f8 100%)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
      }}>
        <div style={{
          ...cardElevated, maxWidth: 480, width: '100%',
          padding: isMobile ? 24 : 40, boxShadow: t.shadowLg,
        }}>
          {/* Header */}
          <div style={{ textAlign: 'center', marginBottom: 28 }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
              background: t.gradientAccent,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 6px 20px rgba(59,130,246,0.3)',
            }}>
              <User size={26} color="#fff" />
            </div>
            <h2 style={{ color: t.text, margin: '0 0 4px', fontSize: 20, fontWeight: 800 }}>
              Welcome to DFW Portal
            </h2>
            <p style={{ color: t.textSecondary, margin: 0, fontSize: 14 }}>
              Set up your profile to get started. This only needs to be done once.
            </p>
          </div>

          <form onSubmit={handleIdentitySave}>
            <div style={{ marginBottom: 16 }}>
              <label style={label}>Full Name</label>
              <input type="text" value={workerName} onChange={e => setWorkerName(e.target.value)}
                placeholder="John Smith" style={input} required />
            </div>
            <div style={{ marginBottom: 16 }}>
              <label style={label}>Email Address</label>
              <input type="email" value={workerEmail} onChange={e => setWorkerEmail(e.target.value)}
                placeholder="john@example.com" style={input} required />
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={label}>Your Role</label>
              <select value={workerRole} onChange={e => setWorkerRole(e.target.value)} style={select} required>
                <option value="">Select your role...</option>
                {Object.entries(cfg.roles).map(([key, r]) => (
                  <option key={key} value={key}>{r.title} — ${r.rate.toFixed(2)}/hr</option>
                ))}
              </select>
            </div>
            <button type="submit" style={btnPrimary}>
              <ChevronRight size={18} /> Continue to Portal
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ══════════════════════════════════════════════
  // TAB DEFINITIONS
  // ══════════════════════════════════════════════
  const tabs = [
    { id: 'report', label: 'Daily Report', shortLabel: 'Report', icon: <FileText size={18} /> },
    { id: 'compliance', label: 'Compliance', shortLabel: 'Compliance', icon: <Shield size={18} /> },
    { id: 'rules', label: 'Site Rules', shortLabel: 'Rules', icon: <AlertTriangle size={18} /> },
    { id: 'info', label: 'Project Info', shortLabel: 'Info', icon: <Briefcase size={18} /> },
  ];

  const weekly = getWeeklySummary();

  // ══════════════════════════════════════════════
  // STAT CARD COMPONENT
  // ══════════════════════════════════════════════
  const StatCard = ({ label: lbl, value, icon, gradient, subtitle }) => (
    <div style={{
      flex: isMobile ? '1 1 calc(50% - 6px)' : '1 1 0',
      minWidth: isMobile ? 'calc(50% - 6px)' : 140,
      background: gradient || t.cardBg,
      borderRadius: 14,
      padding: isMobile ? '14px 12px' : '18px 16px',
      border: gradient ? 'none' : `1px solid ${t.border}`,
      boxShadow: gradient ? '0 4px 16px rgba(0,0,0,0.15)' : t.shadow,
      position: 'relative',
      overflow: 'hidden',
    }}>
      {gradient && (
        <div style={{
          position: 'absolute', top: 0, right: 0, width: 60, height: 60,
          background: 'rgba(255,255,255,0.08)', borderRadius: '0 0 0 60px',
        }} />
      )}
      <div style={{
        color: gradient ? 'rgba(255,255,255,0.8)' : t.textSecondary,
        marginBottom: 6,
      }}>
        {icon}
      </div>
      <div style={{
        fontSize: isMobile ? 22 : 26,
        fontWeight: 800,
        color: gradient ? '#fff' : t.text,
        letterSpacing: '-0.02em',
        lineHeight: 1,
      }}>
        {value}
      </div>
      <div style={{
        fontSize: 11,
        color: gradient ? 'rgba(255,255,255,0.7)' : t.textSecondary,
        marginTop: 4,
        fontWeight: 500,
        textTransform: 'uppercase',
        letterSpacing: '0.05em',
      }}>
        {lbl}
      </div>
      {subtitle && (
        <div style={{
          fontSize: 11,
          color: gradient ? 'rgba(255,255,255,0.5)' : t.textMuted,
          marginTop: 2,
        }}>
          {subtitle}
        </div>
      )}
    </div>
  );

  // ══════════════════════════════════════════════
  // RENDER: DAILY REPORT TAB
  // ══════════════════════════════════════════════
  const renderDailyReport = () => (
    <div>
      {/* Weekly Summary Cards */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: isMobile ? 8 : 12, marginBottom: 20 }}>
        <StatCard
          label="Hours This Week"
          value={weekly.totalHours.toFixed(1)}
          icon={<Clock size={18} />}
          gradient="linear-gradient(135deg, #3b82f6, #6366f1)"
          subtitle={`${weekly.count} ${weekly.count === 1 ? 'shift' : 'shifts'}`}
        />
        <StatCard
          label="Weekly Pay"
          value={`$${weekly.totalPay.toFixed(0)}`}
          icon={<DollarSign size={18} />}
          gradient="linear-gradient(135deg, #059669, #10b981)"
          subtitle={`$${hourlyRate.toFixed(2)}/hr`}
        />
        <StatCard
          label="Cable Pulled"
          value={`${weekly.totalCable.toFixed(0)}'`}
          icon={<Ruler size={18} />}
        />
        <StatCard
          label="Splices"
          value={weekly.totalSplices}
          icon={<Zap size={18} />}
        />
      </div>

      {/* Daily Report Form */}
      <div style={cardElevated}>
        {sectionHeader('Submit Daily Report', <FileText size={18} color={t.accent} />)}
        <div style={{
          display: 'inline-flex', alignItems: 'center', gap: 8,
          background: t.accentLight, borderRadius: 8, padding: '6px 12px',
          marginBottom: 20,
        }}>
          <Award size={14} color={t.accent} />
          <span style={{ color: t.accent, fontSize: 13, fontWeight: 600 }}>
            {roleInfo.title} — ${hourlyRate.toFixed(2)}/hr
          </span>
        </div>

        <form onSubmit={handleSubmitReport}>
          {/* Date & Time */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: t.textSecondary, marginBottom: 10,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Calendar size={14} /> Shift Details
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr', gap: 10 }}>
              <div style={{ gridColumn: isMobile ? 'span 2' : 'span 1' }}>
                <label style={labelNormal}>Date</label>
                <input type="date" value={reportDate} onChange={e => setReportDate(e.target.value)} style={input} required />
              </div>
              <div>
                <label style={labelNormal}>Start Time</label>
                <input type="time" value={startTime} onChange={e => setStartTime(e.target.value)} style={input} />
              </div>
              <div>
                <label style={labelNormal}>End Time</label>
                <input type="time" value={endTime} onChange={e => setEndTime(e.target.value)} style={input} />
              </div>
              {!isMobile && (
                <div>
                  <label style={labelNormal}>Hours Worked</label>
                  <input type="number" step="0.25" min="0" max="24" value={hoursWorked}
                    onChange={e => setHoursWorked(e.target.value)} placeholder="8.0" style={input} required />
                </div>
              )}
            </div>
            {isMobile && (
              <div style={{ marginTop: 10 }}>
                <label style={labelNormal}>Hours Worked</label>
                <input type="number" step="0.25" min="0" max="24" value={hoursWorked}
                  onChange={e => setHoursWorked(e.target.value)} placeholder="8.0" style={input} required />
              </div>
            )}
          </div>

          {/* Pay Calculation */}
          {hoursWorked && parseFloat(hoursWorked) > 0 && (
            <div style={{
              background: t.successBg,
              borderRadius: 12,
              padding: '14px 16px',
              marginBottom: 20,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              border: `1px solid ${darkMode ? '#065f46' : '#a7f3d0'}`,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <DollarSign size={18} color={t.success} />
                <span style={{ color: t.successText, fontWeight: 500, fontSize: 14 }}>
                  {parseFloat(hoursWorked).toFixed(2)} hrs x ${hourlyRate.toFixed(2)}
                </span>
              </div>
              <span style={{
                color: t.success, fontWeight: 800, fontSize: 22,
                letterSpacing: '-0.02em',
              }}>
                ${(parseFloat(hoursWorked) * hourlyRate).toFixed(2)}
              </span>
            </div>
          )}

          {/* Area */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: t.textSecondary, marginBottom: 10,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <MapPin size={14} /> Location
            </div>
            <select value={area} onChange={e => setArea(e.target.value)} style={select} required>
              <option value="">Select area worked...</option>
              {cfg.areas.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>

          {/* Work Types */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: t.textSecondary, marginBottom: 10,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Wrench size={14} /> Work Performed
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {cfg.workTypes.map(wt => (
                <span key={wt} onClick={() => toggleWorkType(wt)} style={chip(selectedWorkTypes.includes(wt))}>
                  {selectedWorkTypes.includes(wt) && <Check size={12} />}
                  {wt}
                </span>
              ))}
            </div>
          </div>

          {/* Production Quantities */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: t.textSecondary, marginBottom: 10,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Activity size={14} /> Production Quantities
            </div>
            <div style={{
              display: 'grid',
              gridTemplateColumns: isMobile ? '1fr 1fr' : '1fr 1fr 1fr 1fr',
              gap: 10,
              background: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
              borderRadius: 12,
              padding: 12,
              border: `1px solid ${t.borderLight}`,
            }}>
              {[
                { label: 'Cable (ft)', val: cablePulled, set: setCablePulled, step: '1', icon: '🔌' },
                { label: 'Splices', val: splicesCompleted, set: setSplicesCompleted, icon: '⚡' },
                { label: 'Tray (ft)', val: traysInstalled, set: setTraysInstalled, step: '1', icon: '📐' },
                { label: 'J-Hooks', val: jhooksInstalled, set: setJhooksInstalled, icon: '🪝' },
                { label: 'Terms', val: termsCompleted, set: setTermsCompleted, icon: '🔗' },
                { label: 'Tests', val: testsCompleted, set: setTestsCompleted, icon: '📊' },
                { label: 'Fire Sleeves', val: sleevesInstalled, set: setSleevesInstalled, icon: '🔥' },
              ].map(({ label: l, val, set, step, icon }) => (
                <div key={l}>
                  <label style={{ ...labelNormal, fontSize: 12 }}>{icon} {l}</label>
                  <input type="number" step={step || '1'} min="0" value={val}
                    onChange={e => set(e.target.value)} placeholder="0"
                    style={{ ...input, padding: '9px 10px', fontSize: 14, textAlign: 'center' }} />
                </div>
              ))}
            </div>
          </div>

          {/* Equipment Used */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: t.textSecondary, marginBottom: 10,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <HardHat size={14} /> Equipment Used
            </div>
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {cfg.equipment.map(eq => (
                <span key={eq} onClick={() => toggleEquipment(eq)} style={chip(equipmentUsed.includes(eq))}>
                  {equipmentUsed.includes(eq) && <Check size={12} />}
                  {eq}
                </span>
              ))}
            </div>
          </div>

          {/* Safety & Incidents */}
          <div style={{ marginBottom: 20 }}>
            <div style={{
              fontSize: 13, fontWeight: 700, color: t.textSecondary, marginBottom: 10,
              textTransform: 'uppercase', letterSpacing: '0.05em',
              display: 'flex', alignItems: 'center', gap: 6,
            }}>
              <Shield size={14} /> Safety & Notes
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '1fr 1fr', gap: 10 }}>
              <div>
                <label style={labelNormal}>Safety Notes / Toolbox Talk</label>
                <textarea value={safetyNotes} onChange={e => setSafetyNotes(e.target.value)}
                  placeholder="Safety observations, toolbox talk topics..."
                  rows={3} style={{ ...input, resize: 'vertical', minHeight: 70 }} />
              </div>
              <div>
                <label style={{ ...labelNormal, color: t.danger }}>Incidents / Near-Misses</label>
                <textarea value={incidents} onChange={e => setIncidents(e.target.value)}
                  placeholder="Report any incidents, injuries, or near-misses..."
                  rows={3} style={{
                    ...input, resize: 'vertical', minHeight: 70,
                    borderColor: incidents ? t.danger : t.border,
                  }} />
              </div>
            </div>
            <div style={{ marginTop: 10 }}>
              <label style={labelNormal}>Additional Notes</label>
              <textarea value={reportNotes} onChange={e => setReportNotes(e.target.value)}
                placeholder="Anything else about tonight's shift..."
                rows={2} style={{ ...input, resize: 'vertical' }} />
            </div>
          </div>

          {/* Site rules acknowledgment reminder */}
          {!rulesAcknowledged && (
            <div style={{
              background: t.warningBg,
              border: `1px solid ${darkMode ? '#92400e' : '#fcd34d'}`,
              borderRadius: 12,
              padding: 14,
              marginBottom: 20,
              display: 'flex',
              alignItems: 'center',
              gap: 10,
            }}>
              <AlertTriangle size={20} color={t.warning} style={{ flexShrink: 0 }} />
              <span style={{ color: t.warningText, fontSize: 13, lineHeight: 1.4 }}>
                You must acknowledge the{' '}
                <span
                  style={{ cursor: 'pointer', textDecoration: 'underline', fontWeight: 700 }}
                  onClick={() => setActiveTab('rules')}
                >
                  DFW Site Rules
                </span>{' '}
                before submitting your first report.
              </span>
            </div>
          )}

          <button type="submit" disabled={submitting} style={btnPrimary}>
            {submitting ? <Loader size={18} className="spin" /> : <Send size={18} />}
            {submitting ? 'Submitting...' : 'Submit Daily Report'}
          </button>
        </form>
      </div>

      {/* Report History */}
      <div style={card}>
        <div
          onClick={() => setShowHistory(!showHistory)}
          style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            cursor: 'pointer', padding: '4px 0',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{
              width: 32, height: 32, borderRadius: 8,
              background: t.accentLight,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Calendar size={16} color={t.accent} />
            </div>
            <div>
              <h3 style={{ color: t.text, margin: 0, fontSize: 15, fontWeight: 700 }}>Report History</h3>
              <span style={{ color: t.textSecondary, fontSize: 12 }}>{reports.length} reports submitted</span>
            </div>
          </div>
          <ChevronDown size={20} color={t.textSecondary}
            style={{ transform: showHistory ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s ease' }} />
        </div>

        {showHistory && (
          <div style={{ marginTop: 16 }}>
            {historyLoading ? (
              <div style={{ textAlign: 'center', padding: 32, color: t.textSecondary }}>
                <Loader size={24} className="spin" />
                <p style={{ marginTop: 8 }}>Loading history...</p>
              </div>
            ) : reports.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 32, color: t.textSecondary }}>
                <FileText size={32} style={{ opacity: 0.4, marginBottom: 8 }} />
                <p>No reports submitted yet</p>
              </div>
            ) : (
              <div>
                <div style={{ overflowX: 'auto', margin: '0 -4px' }}>
                  <table style={{ width: '100%', borderCollapse: 'separate', borderSpacing: '0 4px', fontSize: 13 }}>
                    <thead>
                      <tr>
                        {['Date', 'Hours', 'Pay', 'Area', 'Cable', 'Splices', 'Status'].map(h => (
                          <th key={h} style={{
                            padding: '8px 8px', textAlign: 'left', color: t.textSecondary,
                            fontWeight: 700, fontSize: 11, textTransform: 'uppercase',
                            letterSpacing: '0.05em', whiteSpace: 'nowrap',
                          }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {reports.slice(0, 20).map((r, i) => (
                        <tr key={r.id || i} style={{
                          background: i % 2 === 0 ? (darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)') : 'transparent',
                          borderRadius: 8,
                        }}>
                          <td style={{ padding: '10px 8px', color: t.text, whiteSpace: 'nowrap', fontWeight: 600 }}>
                            {new Date(r.work_date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                          </td>
                          <td style={{ padding: '10px 8px', color: t.text, fontWeight: 600 }}>
                            {parseFloat(r.hours_worked).toFixed(1)}
                          </td>
                          <td style={{ padding: '10px 8px', color: t.success, fontWeight: 700 }}>
                            ${parseFloat(r.total_pay || 0).toFixed(0)}
                          </td>
                          <td style={{ padding: '10px 8px', color: t.textSecondary, fontSize: 12 }}>{r.area || '-'}</td>
                          <td style={{ padding: '10px 8px', color: t.text }}>
                            {parseFloat(r.cable_pulled_ft || 0) > 0 ? `${parseFloat(r.cable_pulled_ft).toFixed(0)}'` : '-'}
                          </td>
                          <td style={{ padding: '10px 8px', color: t.text }}>{parseInt(r.splices_completed || 0) || '-'}</td>
                          <td style={{ padding: '10px 8px' }}>
                            <span style={{
                              padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                              background: r.status === 'Approved' ? t.successBg : t.accentLight,
                              color: r.status === 'Approved' ? t.success : t.accent,
                              letterSpacing: '0.02em',
                            }}>{r.status}</span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Totals bar */}
                <div style={{
                  marginTop: 12, padding: '12px 16px',
                  background: darkMode ? 'rgba(59,130,246,0.08)' : 'rgba(59,130,246,0.04)',
                  borderRadius: 10,
                  border: `1px solid ${t.borderLight}`,
                  display: 'flex', justifyContent: 'space-around', flexWrap: 'wrap', gap: 12,
                }}>
                  {[
                    { label: 'Total Hours', value: reports.reduce((s, r) => s + parseFloat(r.hours_worked || 0), 0).toFixed(1) },
                    { label: 'Total Pay', value: '$' + reports.reduce((s, r) => s + parseFloat(r.total_pay || 0), 0).toFixed(0), color: t.success },
                    { label: 'Cable', value: reports.reduce((s, r) => s + parseFloat(r.cable_pulled_ft || 0), 0).toFixed(0) + "'" },
                    { label: 'Splices', value: reports.reduce((s, r) => s + parseInt(r.splices_completed || 0), 0) },
                  ].map(({ label: l, value: v, color }) => (
                    <div key={l} style={{ textAlign: 'center' }}>
                      <div style={{ fontSize: 16, fontWeight: 800, color: color || t.text, letterSpacing: '-0.01em' }}>{v}</div>
                      <div style={{ fontSize: 10, color: t.textSecondary, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{l}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );

  // ══════════════════════════════════════════════
  // RENDER: COMPLIANCE TAB
  // ══════════════════════════════════════════════
  const renderCompliance = () => {
    const categories = [...new Set(cfg.complianceItems.map(i => i.category))];
    const categoryIcons = {
      'Airport Badge': <Shield size={18} color={t.accent} />,
      'Certifications': <Award size={18} color={t.warning} />,
      'Documents': <FileText size={18} color={t.success} />,
    };

    return (
      <div>
        {/* Progress Card with Ring */}
        <div style={{
          ...cardElevated,
          display: 'flex',
          alignItems: 'center',
          gap: isMobile ? 16 : 24,
          padding: isMobile ? 20 : 28,
        }}>
          <ProgressRing
            pct={compliancePct}
            size={isMobile ? 70 : 88}
            stroke={isMobile ? 5 : 6}
            color={compliancePct === 100 ? t.success : t.accent}
            trackColor={darkMode ? '#253347' : '#e2e8f0'}
            textColor={t.text}
          />
          <div style={{ flex: 1 }}>
            <h3 style={{
              color: t.text, margin: '0 0 4px', fontSize: isMobile ? 17 : 19, fontWeight: 800,
            }}>
              {compliancePct === 100 ? 'All Clear!' : 'Compliance Progress'}
            </h3>
            <p style={{ color: t.textSecondary, fontSize: 13, margin: '0 0 10px' }}>
              {complianceCount} of {complianceTotal} items complete
            </p>
            <div style={{
              height: 6, background: darkMode ? '#253347' : '#e2e8f0',
              borderRadius: 3, overflow: 'hidden',
            }}>
              <div style={{
                height: '100%',
                width: `${compliancePct}%`,
                background: compliancePct === 100 ? t.gradientSuccess : t.gradientAccent,
                borderRadius: 3,
                transition: 'width 0.5s ease',
              }} />
            </div>
          </div>
        </div>

        {/* Items by category */}
        {categories.map(cat => {
          const items = cfg.complianceItems.filter(i => i.category === cat).sort((a, b) => a.order - b.order);
          const catDone = items.filter(i => compliance[i.id]).length;
          return (
            <div key={cat} style={card}>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                marginBottom: 16,
              }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 36, height: 36, borderRadius: 10,
                    background: cat === 'Airport Badge' ? t.accentLight
                      : cat === 'Certifications' ? t.warningBg : t.successBg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                  }}>
                    {categoryIcons[cat] || <FileText size={18} />}
                  </div>
                  <h4 style={{ color: t.text, margin: 0, fontSize: 15, fontWeight: 700 }}>{cat}</h4>
                </div>
                <span style={{
                  padding: '4px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                  background: catDone === items.length ? t.successBg : t.accentLight,
                  color: catDone === items.length ? t.success : t.accent,
                }}>
                  {catDone}/{items.length}
                </span>
              </div>
              {items.map((item, idx) => (
                <div key={item.id} onClick={() => toggleCompliance(item.id)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 12, padding: '12px 0',
                    borderBottom: idx < items.length - 1 ? `1px solid ${t.borderLight}` : 'none',
                    cursor: 'pointer',
                    transition: 'opacity 0.15s',
                  }}>
                  <div style={{
                    width: 26, height: 26, borderRadius: 8,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    border: compliance[item.id] ? 'none' : `2px solid ${t.border}`,
                    background: compliance[item.id] ? t.gradientSuccess : 'transparent',
                    transition: 'all 0.2s ease',
                    flexShrink: 0,
                    boxShadow: compliance[item.id] ? '0 2px 6px rgba(16,185,129,0.3)' : 'none',
                  }}>
                    {compliance[item.id] && <Check size={14} color="#fff" strokeWidth={3} />}
                  </div>
                  <span style={{
                    color: compliance[item.id] ? t.textSecondary : t.text,
                    textDecoration: compliance[item.id] ? 'line-through' : 'none',
                    fontSize: 14, fontWeight: compliance[item.id] ? 400 : 500,
                    transition: 'all 0.15s',
                  }}>{item.label}</span>
                </div>
              ))}
            </div>
          );
        })}

        <div style={{
          ...card, borderColor: darkMode ? '#253347' : '#c7d2fe',
          background: darkMode ? 'rgba(59,130,246,0.06)' : '#eef2ff',
          display: 'flex', alignItems: 'flex-start', gap: 10,
        }}>
          <Info size={16} color={t.accent} style={{ flexShrink: 0, marginTop: 1 }} />
          <p style={{ color: t.textSecondary, fontSize: 13, margin: 0, lineHeight: 1.5 }}>
            This checklist is for your tracking. Your supervisor verifies actual compliance.
            Contact <strong>Dayna Roy</strong> at{' '}
            <a href="tel:+12813897827" style={{ color: t.accent, textDecoration: 'none' }}>(281) 389-7827</a>{' '}
            for help with any items.
          </p>
        </div>
      </div>
    );
  };

  // ══════════════════════════════════════════════
  // RENDER: SITE RULES TAB
  // ══════════════════════════════════════════════
  const renderSiteRules = () => {
    const categoryColors = {
      'Schedule': { bg: t.accentLight, color: t.accent, icon: <Clock size={16} color={t.accent} /> },
      'Airport Security & Badge': { bg: t.warningBg, color: t.warning, icon: <Shield size={16} color={t.warning} /> },
      'Scissor Lift & Equipment Safety': { bg: darkMode ? 'rgba(139,92,246,0.1)' : '#f3e8ff', color: '#8b5cf6', icon: <HardHat size={16} color="#8b5cf6" /> },
      'Safety & Incident Reporting': { bg: t.dangerBg, color: t.danger, icon: <AlertTriangle size={16} color={t.danger} /> },
      'Pay & Employment': { bg: t.successBg, color: t.success, icon: <DollarSign size={16} color={t.success} /> },
      'Confidentiality': { bg: darkMode ? 'rgba(59,130,246,0.1)' : '#eff6ff', color: t.accent, icon: <Lock size={16} color={t.accent} /> },
    };

    return (
      <div>
        <div style={cardElevated}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 4 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: t.warningBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <AlertTriangle size={20} color={t.warning} />
            </div>
            <div>
              <h3 style={{ color: t.text, margin: 0, fontSize: 18, fontWeight: 800 }}>DFW Airport Site Rules</h3>
              <p style={{ color: t.textSecondary, fontSize: 13, margin: '2px 0 0' }}>
                {cfg.siteRules.reduce((s, g) => s + g.rules.length, 0)} rules across {cfg.siteRules.length} categories
              </p>
            </div>
          </div>
          <p style={{ color: t.textSecondary, fontSize: 13, margin: '12px 0 0', lineHeight: 1.5 }}>
            Read and acknowledge all rules below before submitting daily reports.
            Violations may result in badge revocation or removal from the project.
          </p>
        </div>

        {cfg.siteRules.map((group, gi) => {
          const catStyle = categoryColors[group.category] || { bg: t.accentLight, color: t.accent, icon: <Info size={16} /> };
          const isOpen = expandedRuleCategory === gi;

          return (
            <div key={gi} style={{
              ...card, padding: 0, overflow: 'hidden',
              borderColor: isOpen ? (darkMode ? catStyle.color + '40' : catStyle.color + '30') : t.border,
              transition: 'border-color 0.2s',
            }}>
              <div
                onClick={() => setExpandedRuleCategory(isOpen ? null : gi)}
                style={{
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  padding: isMobile ? '14px 16px' : '16px 20px',
                  cursor: 'pointer',
                  background: isOpen ? (darkMode ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.01)') : 'transparent',
                  transition: 'background 0.2s',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div style={{
                    width: 32, height: 32, borderRadius: 8,
                    background: catStyle.bg,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                  }}>
                    {catStyle.icon}
                  </div>
                  <span style={{ color: t.text, fontWeight: 700, fontSize: 14 }}>{group.category}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{
                    padding: '3px 8px', borderRadius: 12, fontSize: 11, fontWeight: 600,
                    background: catStyle.bg, color: catStyle.color,
                  }}>
                    {group.rules.length}
                  </span>
                  <ChevronDown size={18} color={t.textSecondary}
                    style={{ transform: isOpen ? 'rotate(180deg)' : 'none', transition: 'transform 0.25s ease' }} />
                </div>
              </div>

              {isOpen && (
                <div style={{ padding: '0 16px 16px', paddingTop: 0 }}>
                  <div style={{ borderTop: `1px solid ${t.borderLight}`, paddingTop: 8 }}>
                    {group.rules.map((rule, ri) => (
                      <div key={ri} style={{
                        display: 'flex', gap: 10, padding: '10px 4px',
                        borderBottom: ri < group.rules.length - 1 ? `1px solid ${t.borderLight}` : 'none',
                      }}>
                        <div style={{
                          width: 20, height: 20, borderRadius: 6,
                          background: catStyle.bg,
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          flexShrink: 0, marginTop: 1,
                        }}>
                          <ChevronRight size={12} color={catStyle.color} />
                        </div>
                        <span style={{ color: t.text, fontSize: 13, lineHeight: 1.6 }}>{rule}</span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          );
        })}

        {/* Acknowledgment */}
        <div style={{
          ...cardElevated,
          background: rulesAcknowledged ? t.successBg : t.warningBg,
          borderColor: rulesAcknowledged ? (darkMode ? '#065f46' : '#a7f3d0') : (darkMode ? '#92400e' : '#fcd34d'),
          padding: isMobile ? 20 : 24,
        }}>
          {rulesAcknowledged ? (
            <div style={{ display: 'flex', alignItems: 'center', gap: 14 }}>
              <div style={{
                width: 48, height: 48, borderRadius: 14,
                background: t.gradientSuccess,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 4px 12px rgba(16,185,129,0.25)',
                flexShrink: 0,
              }}>
                <CheckCircle size={24} color="#fff" />
              </div>
              <div>
                <p style={{ color: t.successText, fontWeight: 800, margin: 0, fontSize: 16 }}>
                  Site Rules Acknowledged
                </p>
                <p style={{ color: t.textSecondary, fontSize: 13, margin: '4px 0 0' }}>
                  You can review the rules anytime by expanding the sections above.
                </p>
              </div>
            </div>
          ) : (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12 }}>
                <AlertTriangle size={22} color={t.warning} />
                <span style={{ color: t.warningText, fontWeight: 800, fontSize: 16 }}>
                  Acknowledgment Required
                </span>
              </div>
              <p style={{ color: t.warningText, fontSize: 13, margin: '0 0 20px', lineHeight: 1.6 }}>
                By clicking below, I confirm that I have read and understand ALL of the DFW Airport site rules
                listed above. I agree to comply with these rules at all times while working on this project.
                I understand that violations may result in badge revocation, removal from the project,
                and/or termination of my engagement.
              </p>
              <button onClick={handleAcknowledgeRules} style={{
                ...btnPrimary,
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                boxShadow: '0 4px 14px rgba(245,158,11,0.35)',
              }}>
                <CheckCircle size={18} /> I Acknowledge the DFW Site Rules
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  // ══════════════════════════════════════════════
  // RENDER: PROJECT INFO TAB
  // ══════════════════════════════════════════════
  const renderProjectInfo = () => (
    <div>
      {/* Rate Card */}
      <div style={{
        ...cardElevated,
        background: t.gradientAccent,
        border: 'none',
        padding: isMobile ? 20 : 28,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -20, right: -20, width: 120, height: 120,
          background: 'rgba(255,255,255,0.06)', borderRadius: '50%',
        }} />
        <div style={{
          position: 'absolute', bottom: -30, left: -30, width: 80, height: 80,
          background: 'rgba(255,255,255,0.04)', borderRadius: '50%',
        }} />
        <div style={{ position: 'relative' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <div>
              <p style={{ color: 'rgba(255,255,255,0.7)', margin: '0 0 4px', fontSize: 13, fontWeight: 500 }}>
                Your Hourly Rate
              </p>
              <p style={{ color: '#fff', margin: '0 0 4px', fontSize: 36, fontWeight: 900, letterSpacing: '-0.03em', lineHeight: 1 }}>
                ${hourlyRate.toFixed(2)}
                <span style={{ fontSize: 16, fontWeight: 400, opacity: 0.7 }}>/hr</span>
              </p>
            </div>
            <div style={{
              background: 'rgba(255,255,255,0.15)',
              borderRadius: 12, padding: '8px 14px',
              backdropFilter: 'blur(4px)',
            }}>
              <p style={{ color: '#fff', margin: 0, fontWeight: 700, fontSize: 13 }}>{roleInfo.title}</p>
              <p style={{ color: 'rgba(255,255,255,0.7)', margin: '2px 0 0', fontSize: 11 }}>1099 | Weekly Pay</p>
            </div>
          </div>
        </div>
      </div>

      {/* Project Overview */}
      <div style={card}>
        {sectionHeader('Project Overview', <Target size={18} color={t.accent} />)}
        <div style={{ display: 'grid', gap: 12 }}>
          {[
            { label: 'Project', value: cfg.project, icon: <Briefcase size={14} color={t.textSecondary} /> },
            { label: 'Client', value: cfg.client, icon: <User size={14} color={t.textSecondary} /> },
            { label: 'Prime Contractor', value: cfg.prime, icon: <Activity size={14} color={t.textSecondary} /> },
          ].map(({ label: l, value: v, icon }) => (
            <div key={l} style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 28, height: 28, borderRadius: 7,
                background: darkMode ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)',
                display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
              }}>{icon}</div>
              <div>
                <span style={{ color: t.textSecondary, fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em', fontWeight: 600 }}>{l}</span>
                <p style={{ color: t.text, fontSize: 14, margin: 0, fontWeight: 500 }}>{v}</p>
              </div>
            </div>
          ))}
        </div>
        <div style={{
          marginTop: 16, padding: '12px 14px', borderRadius: 10,
          background: darkMode ? 'rgba(0,0,0,0.2)' : 'rgba(0,0,0,0.02)',
          border: `1px solid ${t.borderLight}`,
        }}>
          <p style={{ color: t.textSecondary, fontSize: 12, margin: 0, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 4 }}>Scope</p>
          <p style={{ color: t.text, fontSize: 13, margin: 0, lineHeight: 1.6 }}>{cfg.scope}</p>
        </div>
      </div>

      {/* Milestones */}
      <div style={card}>
        {sectionHeader('Milestones', <Target size={18} color={t.accent} />)}
        {cfg.milestones.map((m, i) => {
          const deadline = new Date(m.deadline);
          const today = new Date();
          const daysLeft = Math.ceil((deadline - today) / (1000 * 60 * 60 * 24));
          const isPast = daysLeft < 0;
          const isUrgent = !isPast && daysLeft <= 7;

          return (
            <div key={i} style={{
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
              padding: '14px 0',
              borderBottom: i < cfg.milestones.length - 1 ? `1px solid ${t.borderLight}` : 'none',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: isPast ? t.danger : isUrgent ? t.warning : t.accent,
                  boxShadow: `0 0 0 3px ${isPast ? t.dangerBg : isUrgent ? t.warningBg : t.accentLight}`,
                  flexShrink: 0,
                }} />
                <div>
                  <p style={{ color: t.text, margin: 0, fontWeight: 700, fontSize: 14 }}>{m.description}</p>
                  <p style={{ color: t.textSecondary, margin: '3px 0 0', fontSize: 12 }}>
                    {deadline.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric' })}
                  </p>
                </div>
              </div>
              <span style={{
                padding: '5px 12px', borderRadius: 20, fontSize: 12, fontWeight: 700,
                background: isPast ? t.dangerBg : isUrgent ? t.warningBg : t.accentLight,
                color: isPast ? t.danger : isUrgent ? t.warning : t.accent,
                whiteSpace: 'nowrap',
              }}>
                {isPast ? `${Math.abs(daysLeft)}d overdue` : `${daysLeft} days`}
              </span>
            </div>
          );
        })}
      </div>

      {/* Contacts */}
      <div style={card}>
        {sectionHeader('Key Contacts', <Phone size={18} color={t.accent} />)}
        {cfg.contacts.map((c, i) => (
          <div key={i} style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '14px 0',
            borderBottom: i < cfg.contacts.length - 1 ? `1px solid ${t.borderLight}` : 'none',
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
              <div style={{
                width: 40, height: 40, borderRadius: 12,
                background: t.gradientAccent,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                color: '#fff', fontSize: 15, fontWeight: 800,
                boxShadow: '0 2px 8px rgba(59,130,246,0.2)',
              }}>
                {c.name.split(' ').map(n => n[0]).join('')}
              </div>
              <div>
                <p style={{ color: t.text, margin: 0, fontWeight: 700, fontSize: 14 }}>{c.name}</p>
                <p style={{ color: t.textSecondary, margin: '2px 0 0', fontSize: 12 }}>{c.role}</p>
              </div>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              {c.phone && (
                <a href={`tel:${c.phone.replace(/[^0-9+]/g, '')}`} style={{
                  width: 38, height: 38, borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: t.accentLight,
                  textDecoration: 'none',
                  transition: 'transform 0.1s',
                }}>
                  <Phone size={16} color={t.accent} />
                </a>
              )}
              {c.email && (
                <a href={`mailto:${c.email}`} style={{
                  width: 38, height: 38, borderRadius: 10,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: t.successBg,
                  textDecoration: 'none',
                }}>
                  <Mail size={16} color={t.success} />
                </a>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Your Info */}
      <div style={card}>
        {sectionHeader('Your Info', <User size={18} color={t.accent} />)}
        <div style={{ display: 'grid', gap: 10 }}>
          {[
            ['Name', workerName],
            ['Email', workerEmail],
            ['Role', roleInfo.title || workerRole],
            ['Rate', `$${hourlyRate.toFixed(2)}/hr`],
            ['Pay Schedule', 'Weekly (Direct Deposit)'],
            ['Classification', '1099 Independent Contractor'],
          ].map(([l, v]) => (
            <div key={l} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '6px 0',
            }}>
              <span style={{
                color: t.textSecondary, fontSize: 12, minWidth: 90, flexShrink: 0,
                fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.03em',
              }}>{l}</span>
              <span style={{ color: t.text, fontSize: 14, fontWeight: 500 }}>{v}</span>
            </div>
          ))}
        </div>
        <button onClick={() => { localStorage.removeItem(STORAGE_KEY); setIdentitySet(false); }}
          style={{
            marginTop: 20, padding: '10px 18px', borderRadius: 10,
            border: `1.5px solid ${t.border}`, background: 'transparent',
            color: t.textSecondary, fontSize: 13, cursor: 'pointer', fontWeight: 600,
            transition: 'border-color 0.2s',
          }}>
          Change Identity
        </button>
      </div>
    </div>
  );

  // ══════════════════════════════════════════════
  // MAIN LAYOUT
  // ══════════════════════════════════════════════
  return (
    <div style={{ minHeight: '100vh', background: t.bg }}>
      {/* Header */}
      <div style={{
        position: 'sticky', top: 0, zIndex: 100,
        background: t.gradientHeader,
        padding: '14px 16px',
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          {setCurrentPage && (
            <ArrowLeft size={20} color="rgba(255,255,255,0.7)" style={{ cursor: 'pointer' }}
              onClick={() => setCurrentPage('home')} />
          )}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <h1 style={{ color: '#fff', margin: 0, fontSize: 16, fontWeight: 800, letterSpacing: '-0.01em' }}>
                DFW Airport
              </h1>
              <span style={{
                background: 'rgba(255,255,255,0.12)',
                padding: '2px 8px', borderRadius: 6,
                fontSize: 10, color: 'rgba(255,255,255,0.7)', fontWeight: 600,
                textTransform: 'uppercase', letterSpacing: '0.05em',
              }}>
                LYT
              </span>
            </div>
            <p style={{ color: 'rgba(255,255,255,0.6)', margin: 0, fontSize: 12 }}>
              {workerName} &middot; {roleInfo.title || 'Worker'}
            </p>
          </div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {/* Compliance badge */}
          <div style={{
            padding: '5px 10px', borderRadius: 20, fontSize: 12, fontWeight: 700,
            background: compliancePct === 100
              ? 'rgba(16,185,129,0.2)'
              : 'rgba(245,158,11,0.2)',
            color: compliancePct === 100 ? '#6ee7b7' : '#fcd34d',
            display: 'flex', alignItems: 'center', gap: 4,
          }}>
            {compliancePct === 100 ? <CheckCircle size={12} /> : <Shield size={12} />}
            {compliancePct}%
          </div>
          <button onClick={() => setDarkMode(!darkMode)}
            style={{ background: 'rgba(255,255,255,0.1)', border: 'none', cursor: 'pointer', padding: 6, borderRadius: 8 }}>
            {darkMode ? <Sun size={16} color="rgba(255,255,255,0.7)" /> : <Moon size={16} color="rgba(255,255,255,0.7)" />}
          </button>
        </div>
      </div>

      {/* Tab Bar — desktop: below header, mobile: fixed bottom */}
      <div style={{
        display: 'flex',
        background: t.cardBg,
        borderBottom: isMobile ? 'none' : `1px solid ${t.border}`,
        borderTop: isMobile ? `1px solid ${t.border}` : 'none',
        padding: isMobile ? '4px 8px 2px' : '0 16px',
        position: isMobile ? 'fixed' : 'sticky',
        bottom: isMobile ? 0 : 'auto',
        top: isMobile ? 'auto' : 57,
        left: 0, right: 0, zIndex: 99,
        boxShadow: isMobile ? '0 -2px 12px rgba(0,0,0,0.08)' : 'none',
      }}>
        {tabs.map(tab => {
          const isActive = activeTab === tab.id;
          return (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{
                flex: 1,
                display: 'flex', flexDirection: isMobile ? 'column' : 'row',
                alignItems: 'center', justifyContent: 'center', gap: isMobile ? 3 : 8,
                padding: isMobile ? '8px 4px 10px' : '12px 16px',
                background: 'none', border: 'none', cursor: 'pointer',
                color: isActive ? t.accent : t.textSecondary,
                borderBottom: !isMobile && isActive ? `2.5px solid ${t.accent}` : '2.5px solid transparent',
                borderTop: isMobile && isActive ? `2.5px solid ${t.accent}` : '2.5px solid transparent',
                fontSize: isMobile ? 10 : 13,
                fontWeight: isActive ? 700 : 500,
                whiteSpace: 'nowrap',
                transition: 'color 0.15s',
                position: 'relative',
              }}>
              <span style={{ opacity: isActive ? 1 : 0.6, transition: 'opacity 0.15s' }}>{tab.icon}</span>
              <span>{isMobile ? tab.shortLabel : tab.label}</span>
              {tab.id === 'rules' && !rulesAcknowledged && (
                <span style={{
                  width: 7, height: 7, borderRadius: '50%',
                  background: t.warning, flexShrink: 0,
                  boxShadow: '0 0 0 2px ' + t.cardBg,
                  position: isMobile ? 'absolute' : 'static',
                  top: isMobile ? 6 : 'auto',
                  right: isMobile ? '25%' : 'auto',
                }} />
              )}
            </button>
          );
        })}
      </div>

      {/* Content */}
      <div style={{
        maxWidth: 720,
        margin: '0 auto',
        padding: isMobile ? '16px 14px 88px' : '24px 20px 40px',
      }}>
        {activeTab === 'report' && renderDailyReport()}
        {activeTab === 'compliance' && renderCompliance()}
        {activeTab === 'rules' && renderSiteRules()}
        {activeTab === 'info' && renderProjectInfo()}
      </div>

      {/* Toast */}
      {toast && (
        <div style={{
          position: 'fixed',
          bottom: isMobile ? 76 : 24,
          left: '50%',
          transform: 'translateX(-50%)',
          padding: '14px 24px',
          borderRadius: 14,
          zIndex: 1000,
          background: toast.type === 'error' ? t.danger : t.success,
          color: '#fff',
          fontWeight: 700,
          fontSize: 14,
          boxShadow: '0 8px 32px rgba(0,0,0,0.3)',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          maxWidth: 'calc(100vw - 32px)',
          animation: 'toastIn 0.3s ease',
        }}>
          {toast.type === 'error' ? <XCircle size={18} /> : <CheckCircle size={18} />}
          {toast.msg}
        </div>
      )}

      {/* Animations */}
      <style>{`
        .spin { animation: spin 1s linear infinite; }
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes toastIn {
          from { opacity: 0; transform: translateX(-50%) translateY(16px); }
          to { opacity: 1; transform: translateX(-50%) translateY(0); }
        }
        input:focus, select:focus, textarea:focus {
          border-color: #3b82f6 !important;
          box-shadow: 0 0 0 3px rgba(59,130,246,0.15) !important;
        }
      `}</style>
    </div>
  );
};

export default DFWPortalPage;
